import { SortSettings } from '../User/User.model';

export interface ActiveColumns {
  name: string;
  visible: boolean;
  index: number;
  label: string;
}

export const COLUMN_LABELS = {
  client: 'Customer',
  driver: 'Driver',
  dispatcher: 'Dispatcher',
  pickup: 'Pickup',
  delivery: 'Delivery',
  rate: 'Rate',
  loadId: 'Order #',
  customerOrderNumber: 'Customer Order #',
  orderStatus: 'Order Status',
  tripStatus: 'Trip Status',
  truck: 'Truck',
  trailer: 'Trailer',
  division: 'Division',
  tags: 'Tags',
  weight: 'Weight',
  tripId: 'Trip #',
  invoiceStatus: 'Invoice Status',
  carrier: 'Carrier',
  bookingMode: 'Booking Mode',
  coverage: 'Coverage',
  commodity: 'Commodity',
  quantity: 'Quantity',
  settlementStatus: 'Settlement Status',
  documents: 'Documents',
  pickupLocationName: 'Pickup Location Name',
  pickupAddress: 'Pickup Address',
  pickupCity: 'Pickup City',
  pickupState: 'Pickup State',
  arrivalAtPickup: 'Arrival at Pickup',
  manifestType: 'Border-crossing manifest',
  deliveryLocationName: 'Delivery Location Name',
  deliveryAddress: 'Delivery Address',
  deliveryCity: 'Delivery City',
  deliveryState: 'Delivery State',
  deliveryZip: 'Delivery Zip',
  deliveryCountry: 'Delivery Country',
  arrivalAtDelivery: 'Arrival at Delivery',
  nextStop: 'Next Stop',
  onTimeStatus: 'On-time status',
  dispatchAcceptance: 'Dispatch Acceptance',
  unit: 'Unit',
  dimension: 'L x W x H'
};

export const AVAILABILITY_COLUMN_LABELS = {
  name: 'Name',
  notes: 'Notes',
  currentTrip: 'Current Trip',
  lastCompletedStop: 'Last Completed Stop',
  lastCompletedStopTags: 'Last Stop Tags',
  nextStop: 'Next Stop',
  finalStop: 'Final Stop',
  finalTrip: 'Final Trip',
  status: 'Current Availability'
};

export const DEFAULT_AVAILABILITY_COLUMNS: ActiveColumns[] = [
  { name: 'name', visible: true, index: 0, label: AVAILABILITY_COLUMN_LABELS.name },
  { name: 'notes', visible: true, index: 1, label: AVAILABILITY_COLUMN_LABELS.notes },
  { name: 'currentTrip', visible: true, index: 2, label: AVAILABILITY_COLUMN_LABELS.currentTrip },
  {
    name: 'lastCompletedStop',
    visible: true,
    index: 3,
    label: AVAILABILITY_COLUMN_LABELS.lastCompletedStop
  },

  {
    name: 'lastStopTags',
    visible: true,
    index: 4,
    label: AVAILABILITY_COLUMN_LABELS.lastCompletedStopTags
  },

  { name: 'nextStop', visible: true, index: 5, label: AVAILABILITY_COLUMN_LABELS.nextStop },
  { name: 'finalStop', visible: true, index: 6, label: AVAILABILITY_COLUMN_LABELS.finalStop },
  { name: 'finalTrip', visible: true, index: 7, label: AVAILABILITY_COLUMN_LABELS.finalTrip },
  { name: 'status', visible: true, index: 8, label: AVAILABILITY_COLUMN_LABELS.status }
];

export const DEFAULT_ORDER_COLUMNS: ActiveColumns[] = [
  { name: 'client', visible: true, index: 0, label: COLUMN_LABELS.client },
  { name: 'loadId', visible: true, index: 1, label: COLUMN_LABELS.loadId },
  { name: 'tripId', visible: false, index: 2, label: COLUMN_LABELS.tripId },
  {
    name: 'customerOrderNumber',
    visible: true,
    index: 3,
    label: COLUMN_LABELS.customerOrderNumber
  },
  { name: 'rate', visible: true, index: 3, label: COLUMN_LABELS.rate },
  { name: 'status', visible: true, index: 4, label: COLUMN_LABELS.orderStatus },
  { name: 'invoiceStatus', visible: true, index: 5, label: COLUMN_LABELS.invoiceStatus },
  { name: 'pickupDate', visible: true, index: 6, label: COLUMN_LABELS.pickup },
  { name: 'pickupLocationName', visible: false, index: 7, label: COLUMN_LABELS.pickupLocationName },
  { name: 'pickupAddress', visible: false, index: 8, label: COLUMN_LABELS.pickupAddress },
  { name: 'pickupCity', visible: false, index: 9, label: COLUMN_LABELS.pickupCity },
  { name: 'pickupState', visible: false, index: 10, label: COLUMN_LABELS.pickupState },
  { name: 'arrivalAtPickup', visible: false, index: 11, label: COLUMN_LABELS.arrivalAtPickup },
  { name: 'deliveryDate', visible: true, index: 12, label: COLUMN_LABELS.delivery },
  {
    name: 'deliveryLocationName',
    visible: false,
    index: 13,
    label: COLUMN_LABELS.deliveryLocationName
  },
  { name: 'deliveryAddress', visible: false, index: 14, label: COLUMN_LABELS.deliveryAddress },
  { name: 'deliveryCity', visible: false, index: 15, label: COLUMN_LABELS.deliveryCity },
  { name: 'deliveryState', visible: false, index: 16, label: COLUMN_LABELS.deliveryState },
  { name: 'arrivalAtDelivery', visible: false, index: 17, label: COLUMN_LABELS.arrivalAtDelivery },
  { name: 'tags', visible: true, index: 18, label: COLUMN_LABELS.tags },
  { name: 'driver', visible: true, index: 19, label: COLUMN_LABELS.driver },
  { name: 'truck', visible: true, index: 20, label: COLUMN_LABELS.truck },
  { name: 'commodity', visible: true, index: 21, label: COLUMN_LABELS.commodity },
  { name: 'weight', visible: false, index: 22, label: COLUMN_LABELS.weight },
  { name: 'documents', visible: true, index: 23, label: COLUMN_LABELS.documents },
  { name: 'nextStop', visible: false, index: 24, label: COLUMN_LABELS.nextStop },
  { name: 'trailer', visible: false, index: 25, label: COLUMN_LABELS.trailer },
  { name: 'dispatcher', visible: false, index: 26, label: COLUMN_LABELS.dispatcher },
  { name: 'division', visible: false, index: 27, label: COLUMN_LABELS.division },
  { name: 'quantity', visible: false, index: 28, label: COLUMN_LABELS.quantity },
  { name: 'carrier', visible: false, index: 29, label: COLUMN_LABELS.carrier },
  { name: 'manifestType', visible: false, index: 30, label: COLUMN_LABELS.manifestType },
  { name: 'bookingMode', visible: false, index: 31, label: COLUMN_LABELS.bookingMode },
  { name: 'coverage', visible: false, index: 32, label: COLUMN_LABELS.coverage },
  { name: 'onTimeStatus', visible: false, index: 33, label: COLUMN_LABELS.onTimeStatus },
  { name: 'unit', visible: false, index: 34, label: COLUMN_LABELS.unit },
  { name: 'dimension', visible: false, index: 35, label: COLUMN_LABELS.dimension }
];

export const DEFAULT_TRIP_COLUMNS: ActiveColumns[] = [
  { name: 'client', visible: true, index: 0, label: COLUMN_LABELS.client },
  { name: 'driver', visible: true, index: 1, label: COLUMN_LABELS.driver },
  { name: 'truck', visible: true, index: 2, label: COLUMN_LABELS.truck },
  { name: 'trailer', visible: false, index: 3, label: COLUMN_LABELS.trailer },
  { name: 'dispatchAcceptance', visible: false, index: 4, label: COLUMN_LABELS.dispatchAcceptance },
  { name: 'status', visible: true, index: 5, label: COLUMN_LABELS.tripStatus },
  { name: 'pickupDate', visible: true, index: 6, label: COLUMN_LABELS.pickup },
  { name: 'pickupLocationName', visible: false, index: 7, label: COLUMN_LABELS.pickupLocationName },
  { name: 'pickupAddress', visible: false, index: 8, label: COLUMN_LABELS.pickupAddress },
  { name: 'pickupCity', visible: false, index: 9, label: COLUMN_LABELS.pickupCity },
  { name: 'pickupState', visible: false, index: 10, label: COLUMN_LABELS.pickupState },
  { name: 'arrivalAtPickup', visible: false, index: 11, label: COLUMN_LABELS.arrivalAtPickup },
  { name: 'deliveryDate', visible: true, index: 12, label: COLUMN_LABELS.delivery },
  { name: 'deliveryAddress', visible: false, index: 13, label: COLUMN_LABELS.deliveryAddress },
  {
    name: 'deliveryLocationName',
    visible: false,
    index: 14,
    label: COLUMN_LABELS.deliveryLocationName
  },
  { name: 'deliveryCity', visible: false, index: 15, label: COLUMN_LABELS.deliveryCity },
  { name: 'deliveryState', visible: false, index: 16, label: COLUMN_LABELS.deliveryState },
  { name: 'arrivalAtDelivery', visible: false, index: 17, label: COLUMN_LABELS.arrivalAtDelivery },
  { name: 'nextStop', visible: false, index: 18, label: COLUMN_LABELS.nextStop },
  { name: 'tripId', visible: true, index: 19, label: COLUMN_LABELS.tripId },
  { name: 'loadId', visible: false, index: 20, label: COLUMN_LABELS.loadId },
  {
    name: 'customerOrderNumber',
    visible: true,
    index: 20,
    label: COLUMN_LABELS.customerOrderNumber
  },
  { name: 'tags', visible: true, index: 22, label: COLUMN_LABELS.tags },
  { name: 'commodity', visible: true, index: 23, label: COLUMN_LABELS.commodity },
  { name: 'weight', visible: false, index: 24, label: COLUMN_LABELS.weight },
  { name: 'quantity', visible: false, index: 25, label: COLUMN_LABELS.quantity },
  { name: 'documents', visible: true, index: 26, label: COLUMN_LABELS.documents },
  { name: 'dispatcher', visible: false, index: 27, label: COLUMN_LABELS.dispatcher },
  { name: 'carrier', visible: false, index: 28, label: COLUMN_LABELS.carrier },
  { name: 'manifestType', visible: false, index: 29, label: COLUMN_LABELS.manifestType },
  { name: 'coverage', visible: false, index: 30, label: COLUMN_LABELS.coverage },
  { name: 'settlementStatus', visible: false, index: 31, label: COLUMN_LABELS.settlementStatus },
  { name: 'onTimeStatus', visible: false, index: 32, label: COLUMN_LABELS.onTimeStatus },
  { name: 'unit', visible: false, index: 34, label: COLUMN_LABELS.unit },
  { name: 'dimension', visible: false, index: 35, label: COLUMN_LABELS.dimension }
];

export const DEFAULT_FUEL_TRANSACTION_COLUMNS: ActiveColumns[] = [
  { name: 'date', visible: true, index: 0, label: 'DATE' },
  { name: 'transaction_id', visible: true, index: 1, label: 'TRANSACTION ID' },
  { name: 'driver', visible: true, index: 2, label: 'DRIVER' },
  { name: 'truck', visible: true, index: 3, label: 'TRUCK' },
  { name: 'state', visible: true, index: 4, label: 'STATE/PROVINCE' },
  { name: 'item', visible: true, index: 5, label: 'ITEM' },
  { name: 'quantity', visible: true, index: 6, label: 'QUANTITY' },
  { name: 'unit_price', visible: true, index: 7, label: 'UNIT PRICE' },
  { name: 'purchase_amount', visible: true, index: 8, label: 'PURCHASE AMOUNT' },
  { name: 'fee', visible: true, index: 9, label: 'TRANSACTION FEES' },
  { name: 'total', visible: true, index: 10, label: 'TOTAL' },
  { name: 'settlement', visible: true, index: 11, label: 'SETTLEMENT' },
  { name: 'source', visible: true, index: 12, label: 'SOURCE' },
  { name: 'division', visible: true, index: 14, label: 'DIVISION' },
  { name: 'created_at', visible: true, index: 15, label: 'SYNCED ON' }
];

export const DEFAULT_INVOICES_COLUMNS: ActiveColumns[] = [
  { name: 'invoiceId', visible: true, index: 0, label: 'Invoice Number' },
  { name: 'billFrom', visible: true, index: 1, label: 'Bill from' },
  { name: 'billTo', visible: true, index: 2, label: 'Bill to' },
  { name: 'invoiceDate', visible: true, index: 3, label: 'Invoice Date' },
  { name: 'dueDate', visible: true, index: 4, label: 'Due Date' },
  { name: 'customerOrderNumber', visible: true, index: 5, label: 'Customer Order #' },
  { name: 'loadId', visible: true, index: 6, label: 'Order Id' },
  { name: 'amount', visible: true, index: 7, label: 'Amount' },
  { name: 'source', visible: true, index: 8, label: 'Source' },
  { name: 'status', visible: true, index: 9, label: 'Status' },
  { name: 'qbDesktopLastSync', visible: true, index: 9, label: 'QBD Last Sync' },
  { name: 'factored', visible: true, index: 10, label: 'Factored' },
  { name: 'lastEditedDate', visible: true, index: 11, label: 'Last edited date' },
  { name: 'icon', visible: true, index: 12, label: 'Alerts' }
];

export const DEFAULT_ITEMS_PER_PAGE = [
  { label: '25', value: '25' },
  { label: '50', value: '50' },
  { label: '100', value: '100' }
];

export const DEFAULT_SORT_SETTINGS: Record<'ORDERS' | 'TRIPS' | 'CARRIERS', SortSettings> = {
  ORDERS: {
    sort_by: 'pickup_date',
    order_by: 'desc'
  },
  TRIPS: {
    sort_by: 'pickup_date',
    order_by: 'desc'
  },
  CARRIERS: {
    sort_by: 'name',
    order_by: 'asc'
  }
};

export const DEFAULT_CARRIER_COLUMNS: ActiveColumns[] = [
  { name: 'name', visible: true, index: 0, label: 'Name' },
  { name: 'address', visible: true, index: 1, label: 'Address' },
  { name: 'emailAddress', visible: true, index: 2, label: 'Email' },
  { name: 'phone', visible: true, index: 3, label: 'Phone Number' },
  { name: 'taxId', visible: true, index: 4, label: 'Tax ID' },
  { name: 'mcNumber', visible: true, index: 5, label: 'MC Number' },
  { name: 'externalCompanyId', visible: true, index: 6, label: 'Carrier ID' },
  { name: 'payTo', visible: true, index: 7, label: 'Pay To' },
  { name: 'pointOfContact', visible: true, index: 8, label: 'Point of Contact' },
  { name: 'payRate', visible: true, index: 9, label: 'Pay Rate' },
  { name: 'notes', visible: true, index: 10, label: 'Notes' }
];
